export interface LayerGroup<T> {
  layerNumber: number;
  materials: T[];
}

export const groupLayersByNumber = <T extends { LayerNumber?: number | null }>(
  layers: T[],
  descending = false,
): LayerGroup<T>[] => {
  const groupsByNumber = new Map<number, T[]>();

  for (const layer of layers) {
    const layerNumber = layer.LayerNumber ?? 0;
    const materials = groupsByNumber.get(layerNumber) ?? [];
    materials.push(layer);
    groupsByNumber.set(layerNumber, materials);
  }

  const groups = Array.from(groupsByNumber, ([layerNumber, materials]) => ({
    layerNumber,
    materials,
  })).sort((a, b) => a.layerNumber - b.layerNumber);

  return descending
    ? groups.reverse().map((group) => ({
        ...group,
        materials: [...group.materials].reverse(),
      }))
    : groups;
};

export const groupConsecutiveLayers = <
  T extends { LayerNumber?: number | null },
>(
  layers: T[],
): LayerGroup<T>[] =>
  layers.reduce<LayerGroup<T>[]>((groups, layer) => {
    const layerNumber = layer.LayerNumber ?? 0;
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.layerNumber === layerNumber) {
      lastGroup.materials.push(layer);
    } else {
      groups.push({ layerNumber, materials: [layer] });
    }
    return groups;
  }, []);

export const isMultiMaterialLayer = <T>(group: LayerGroup<T>) =>
  group.materials.length > 1;

export const toTopFirstOrder = <T>(layers: T[]): T[] => [...layers].reverse();

export const isInSharedLayer = <T extends { LayerNumber?: number | null }>(
  layers: T[],
  layer: T,
  idKey: keyof T,
): boolean =>
  layers.some(
    (other) =>
      other.LayerNumber === layer.LayerNumber && other[idKey] !== layer[idKey],
  );

export const getSiblingCommodityIds = <
  T extends { LayerNumber?: number | null; CommodityId?: number | null },
>(
  layers: T[],
  layer: T,
  idKey: keyof T,
): number[] =>
  layers
    .filter(
      (other) =>
        other.LayerNumber === layer.LayerNumber &&
        other[idKey] !== layer[idKey],
    )
    .map((other) => other.CommodityId)
    .filter((id): id is number => id != null);

export type GroupPosition = "first" | "middle" | "last";

export const getGroupPosition = (
  index: number,
  count: number,
): GroupPosition => {
  if (index === 0) return "first";
  if (index === count - 1) return "last";
  return "middle";
};

export const getLayerGroupPositions = <
  T extends { LayerNumber?: number | null },
>(
  layers: T[],
  idKey: keyof T,
): Map<T[keyof T], GroupPosition> => {
  const positions = new Map<T[keyof T], GroupPosition>();
  for (const group of groupConsecutiveLayers(layers)) {
    if (!isMultiMaterialLayer(group)) continue;
    group.materials.forEach((layer, index) => {
      positions.set(
        layer[idKey],
        getGroupPosition(index, group.materials.length),
      );
    });
  }
  return positions;
};

export interface LayerMovePreview<T> {
  targetMaterials: T[];
  moveIntoLayerNumber: number;
  isCommodityInTarget: boolean;
}

export const getLayerMovePreview = <
  T extends { LayerNumber?: number | null; CommodityId?: number | null },
>(
  layers: T[],
  movedLayer: T,
  targetLayerNumber: number,
  idKey: keyof T,
): LayerMovePreview<T> => {
  const currentLayerNumber = movedLayer.LayerNumber ?? 0;
  const otherLayers = layers.filter(
    (layer) => layer[idKey] !== movedLayer[idKey],
  );
  const isAloneInLayer = !otherLayers.some(
    (layer) => (layer.LayerNumber ?? 0) === currentLayerNumber,
  );
  const targetMaterials = otherLayers.filter(
    (layer) => (layer.LayerNumber ?? 0) === targetLayerNumber,
  );

  return {
    targetMaterials,
    moveIntoLayerNumber:
      isAloneInLayer && targetLayerNumber > currentLayerNumber
        ? targetLayerNumber - 1
        : targetLayerNumber,
    isCommodityInTarget: targetMaterials.some(
      (layer) => layer.CommodityId === movedLayer.CommodityId,
    ),
  };
};
